import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Divider,
    Paper,
    Box,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Close as CloseIcon,
    Description as PdfIcon,
    Image as ImageIcon,
    OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MMMM d, yyyy");
    } catch {
        return dateStr;
    }
}

function fileIcon(title) {
    if (title?.toLowerCase().endsWith(".pdf"))
        return <PdfIcon fontSize="small" color="error" />;
    return <ImageIcon fontSize="small" color="primary" />;
}

function statusColor(status) {
    switch (status) {
        case "Complete":
            return "success";
        case "Working solution provided":
            return "info";
        case "Under Observation":
            return "warning";
        case "Pending":
        case "Incomplete":
            return "error";
        default:
            return "default";
    }
}

function InfoRow({ label, value }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 0.5,
                columnGap: 1,
            }}
        >
            <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ minWidth: 100, flexShrink: 0 }}
            >
                {label}:
            </Typography>
            <Typography variant="body2">{value || "—"}</Typography>
        </Box>
    );
}

function SectionPaper({ title, children }) {
    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2, borderColor: "primary.main", borderRadius: 1 }}
        >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {title}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {children}
        </Paper>
    );
}

export default function DoorServiceReportViewDialog({ open, onClose, report }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    if (!report) return null;

    const {
        number,
        project_detail,
        quote_detail,
        created_by_detail,
        technician_detail,
        report_date,
        status,
        service_type,
        problem_reported,
        service_rendered,
        comments,
        attachments_detail,
        created,
    } = report;

    const linkedLabel = project_detail
        ? `${project_detail.number} — ${project_detail.name}`
        : quote_detail
          ? `${quote_detail.number} — ${quote_detail.name}`
          : "—";

    const createdByName = created_by_detail
        ? created_by_detail.full_name || created_by_detail.username
        : "—";

    const technicianName = technician_detail
        ? technician_detail.full_name || technician_detail.username
        : "—";

    const addressDetail =
        project_detail?.address_detail || quote_detail?.address_detail || null;
    const addressLine = addressDetail
        ? [
              addressDetail.address,
              addressDetail.city,
              addressDetail.state,
              addressDetail.postal_code,
          ]
              .filter(Boolean)
              .join(", ")
        : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Typography variant="h6">
                        Door Service Report — {number}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {status && (
                            <Chip
                                label={status}
                                size="small"
                                color={statusColor(status)}
                            />
                        )}
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <SectionPaper title="Report Information">
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <InfoRow label="Report #" value={number} />
                            <InfoRow
                                label="Report Date"
                                value={formatDate(report_date)}
                            />
                            <InfoRow
                                label="Service Type"
                                value={service_type?.trim()}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow label="Technician" value={technicianName} />
                            <InfoRow label="Created By" value={createdByName} />
                            <InfoRow label="Created" value={formatDate(created)} />
                        </Grid>
                    </Grid>
                </SectionPaper>

                <SectionPaper title={project_detail ? "Project" : "Quote"}>
                    <InfoRow
                        label={project_detail ? "Project" : "Quote"}
                        value={linkedLabel}
                    />
                    {addressLine && (
                        <InfoRow label="Address" value={addressLine} />
                    )}
                </SectionPaper>

                {problem_reported && problem_reported.trim() && (
                    <SectionPaper title="Problem Reported">
                        <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                        >
                            {problem_reported}
                        </Typography>
                    </SectionPaper>
                )}

                {service_rendered && service_rendered.trim() && (
                    <SectionPaper title="Service Rendered">
                        <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                        >
                            {service_rendered}
                        </Typography>
                    </SectionPaper>
                )}

                {comments && comments.trim() && (
                    <SectionPaper title="Comments">
                        <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                        >
                            {comments}
                        </Typography>
                    </SectionPaper>
                )}

                {attachments_detail && attachments_detail.length > 0 && (
                    <SectionPaper title="Attachments">
                        <List dense disablePadding>
                            {attachments_detail.map((doc, idx) => (
                                <ListItem
                                    key={doc.id}
                                    disableGutters
                                    divider={idx < attachments_detail.length - 1}
                                    secondaryAction={
                                        doc.document_url && (
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={doc.document_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        {fileIcon(doc.title)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={doc.title}
                                        primaryTypographyProps={{
                                            variant: "body2",
                                            noWrap: true,
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </SectionPaper>
                )}
            </DialogContent>

            <DialogActions
                sx={
                    fullScreen
                        ? { pb: "max(8px, env(safe-area-inset-bottom))" }
                        : undefined
                }
            >
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
